import { useState, useEffect, useCallback } from "react";
import { Plug, Plus, Trash2, Key, Shield, RefreshCw, Eye, EyeOff, DollarSign, History } from "lucide-react";
import { useStudio } from "@/store/StudioContext";
import { useAuth } from "@/store/AuthContext";
import { Notice, EmptyState } from "@/components/common/States";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import { CONNECTION_PROVIDERS, providerById, syncConnectionsWithCatalog } from "@/domain/connections";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { t } from "@/i18n";

const STAGE_KEYS = ["created", "unconfigured", "unverified", "operational"];

const GENERATION_PROVIDERS = [
  { id: "apimart", label: "Apimart", capabilities: ["image", "video"] },
  { id: "fal", label: "fal.ai", capabilities: ["upscale"] },
  { id: "kie", label: "Kie.ai", capabilities: ["image", "video"] },
  { id: "muapi", label: "MuAPI", capabilities: ["image", "video"] },
];

// Resumen visual de las 4 etapas del ciclo de vida de una conexión.
const LifecycleStrip = () => (
  <div className="flex flex-wrap items-center gap-1.5" data-testid="connections-lifecycle">
    {STAGE_KEYS.map((stage, i) => {
      const reached = i <= 2;
      return (
        <span key={stage} className="flex items-center gap-1.5">
          {i > 0 && <span className="text-[#4a4a4a]">→</span>}
          <span className={`rounded-full border px-2 py-0.5 text-[11px] font-medium ${reached ? "border-[#f5b544] text-[#f5b544]" : "border-[#303030] text-[#6b6b6b]"}`}>
            {t.connections.stages[stage]}
          </span>
        </span>
      );
    })}
  </div>
);

const ConnectionsManager = () => {
  const { state, dispatch, catalog, refreshCatalog } = useStudio();
  const { user } = useAuth();
  const isOwner = user?.role === "owner" || user?.id === "owner" || user?._id === "owner";

  // Proveedores visibles según el rol del usuario
  const visibleGenerationProviders = isOwner
    ? GENERATION_PROVIDERS
    : GENERATION_PROVIDERS.filter((p) => p.id === "apimart");
  const visibleConnectionProviders = isOwner
    ? CONNECTION_PROVIDERS
    : CONNECTION_PROVIDERS.filter((p) => p.id === "apimart");

  // Estado de claves en bóveda
  const [userConnections, setUserConnections] = useState([]);
  const [loadingUserKeys, setLoadingUserKeys] = useState(true);
  const [editingProviderId, setEditingProviderId] = useState(null);
  const [keyInput, setKeyInput] = useState("");
  const [showKeyText, setShowKeyText] = useState(false);
  const [savingKey, setSavingKey] = useState(false);
  const [keyToDelete, setKeyToDelete] = useState(null);

  // Estado de control de gastos / presupuesto
  const [budgetStatus, setBudgetStatus] = useState(null);
  const [loadingBudget, setLoadingBudget] = useState(true);
  const [editingLimit, setEditingLimit] = useState(false);
  const [limitInput, setLimitInput] = useState("");
  const [savingLimit, setSavingLimit] = useState(false);
  const [budgetHistory, setBudgetHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  // Estado de conexiones de estudio
  const [name, setName] = useState("");
  const [providerId, setProviderId] = useState(visibleConnectionProviders[0]?.id || "apimart");
  const [toDelete, setToDelete] = useState(null);
  const provider = providerById(providerId) || visibleConnectionProviders[0] || CONNECTION_PROVIDERS[0];
  const allConnections = syncConnectionsWithCatalog(state.connections, catalog);
  const visibleStudioConnections = isOwner
    ? allConnections
    : allConnections.filter((c) => c.provider === "apimart");

  const fetchUserConnections = useCallback(async () => {
    setLoadingUserKeys(true);
    try {
      const res = await api.listUserConnections();
      setUserConnections(res?.connections || []);
    } catch {
      setUserConnections([]);
    } finally {
      setLoadingUserKeys(false);
    }
  }, []);

  const fetchBudgetStatus = useCallback(async () => {
    setLoadingBudget(true);
    try {
      const res = await api.getBudgetStatus();
      setBudgetStatus(res);
      if (res?.monthly_limit_usd !== null && res?.monthly_limit_usd !== undefined) {
        setLimitInput(String(res.monthly_limit_usd));
      } else {
        setLimitInput("");
      }
    } catch {
      setBudgetStatus(null);
    } finally {
      setLoadingBudget(false);
    }
  }, []);

  const fetchBudgetHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const res = await api.getBudgetHistory(25);
      setBudgetHistory(res?.history || []);
    } catch {
      setBudgetHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  useEffect(() => {
    fetchUserConnections();
    fetchBudgetStatus();
  }, [fetchUserConnections, fetchBudgetStatus]);

  const handleSaveKey = async (provId) => {
    const clean = keyInput.trim();
    if (!clean) {
      toast.error("La clave no puede estar vacía.");
      return;
    }

    setSavingKey(true);
    try {
      await api.saveUserConnection({ provider_id: provId, api_key: clean });
      toast.success(`Clave de ${provId} guardada y protegida en tu bóveda.`);
      setEditingProviderId(null);
      setKeyInput("");
      fetchUserConnections();
      if (typeof refreshCatalog === "function") {
        await refreshCatalog().catch(() => {});
      }
    } catch (err) {
      if (err.message && err.message.includes("active_jobs_in_progress")) {
        toast.error("No se puede reemplazar la clave mientras haya trabajos activos en curso.");
      } else if (err.message && err.message.includes("vault_not_configured")) {
        toast.error("La bóveda de claves no está configurada en el servidor.");
      } else {
        toast.error(err.message || "Error al guardar la clave.");
      }
    } finally {
      setSavingKey(false);
    }
  };

  const handleDeleteKey = async () => {
    if (!keyToDelete) return;
    try {
      await api.deleteUserConnection(keyToDelete.provider_id);
      toast.success(`Clave propia de ${keyToDelete.provider_id} eliminada.`);
      setKeyToDelete(null);
      fetchUserConnections();
      if (typeof refreshCatalog === "function") {
        await refreshCatalog().catch(() => {});
      }
    } catch (err) {
      if (err.message && err.message.includes("active_jobs_in_progress")) {
        toast.error("No se puede eliminar la clave mientras haya trabajos activos en curso.");
      } else {
        toast.error(err.message || "Error al eliminar la clave.");
      }
    }
  };

  const handleSaveLimit = async () => {
    const raw = limitInput.trim();
    if (raw === "") {
      toast.error("Introduce un número válido para tu límite mensual.");
      return;
    }
    const val = Number(raw);
    if (!Number.isFinite(val) || val < 0) {
      toast.error("El límite mensual debe ser un número válido mayor o igual a cero.");
      return;
    }

    setSavingLimit(true);
    try {
      const res = await api.updateBudgetLimit(val);
      toast.success("Límite mensual actualizado correctamente.");
      setBudgetStatus(res?.budget || null);
      setEditingLimit(false);
      fetchBudgetStatus();
    } catch (err) {
      if (err.message && err.message.includes("limit_below_current_usage")) {
        toast.error("No puedes fijar un límite menor al consumo y reservas ya comprometidas este mes.");
      } else if (err.message && err.message.includes("invalid_limit")) {
        toast.error("El límite introducido no es válido. Debe ser un número finito positivo o cero.");
      } else {
        toast.error(err.message || "Error al actualizar el límite mensual.");
      }
    } finally {
      setSavingLimit(false);
    }
  };

  const add = () => {
    if (!provider?.available) return;
    dispatch({
      type: "CONNECTION_CREATE",
      payload: { name: name.trim() || provider.label, provider: provider.id, capabilities: provider.capabilities },
    });
    dispatch({
      type: "ONBOARDING_UPDATE",
      payload: { stepStatus: { ...state.onboarding.stepStatus, connections: "completed" } },
    });
    setName("");
  };

  return (
    <div className="flex flex-col gap-6" data-testid="connections-manager">
      {/* Aviso de Bóveda y Aislamiento */}
      <div className="rounded-xl border border-[#2b2b2b] bg-[#141414] p-4 text-xs text-[#a3a3a3]">
        <div className="flex items-start gap-3">
          <Shield className="mt-0.5 shrink-0 text-[#f5b544]" size={18} />
          <div className="space-y-1">
            <p className="font-semibold text-[#f4f1ec]">Bóveda de Credenciales Personales</p>
            <p>
              Tus claves de generación se guardan cifradas de forma segura en el servidor. Nunca se comparten
              entre usuarios ni se devuelven al navegador.
            </p>
            <p className="text-[11px] text-[#707070]">
              Nota: «Clave configurada» indica que la credencial está lista en tu bóveda. No se realizan llamadas de pago automáticas
              al guardar ni se expone ningún fragmento secreto.
            </p>
          </div>
        </div>
      </div>

      {!isOwner && (
        <Notice tone="info" testId="guest-connections-notice">
          Como usuario invitado, dispones de acceso exclusivo al proveedor Apimart utilizando tu propia clave y un límite mensual personal de uso.
        </Notice>
      )}

      {/* SECCIÓN 1: Claves Propias por Proveedor */}
      <div className="flex flex-col gap-3" data-testid="vault-connections-section">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-[#ededed]">Claves de Generación (Bóveda Personal)</h3>
            <p className="text-xs text-[#8a8a8a]">
              Configura tus credenciales para los proveedores de generación de imagen y vídeo.
            </p>
          </div>
          <button
            type="button"
            onClick={fetchUserConnections}
            disabled={loadingUserKeys}
            title="Refrescar estado de claves"
            className="v-btn-ghost v-btn-sm inline-flex items-center gap-1.5"
          >
            <RefreshCw size={13} className={loadingUserKeys ? "animate-spin text-[#f5b544]" : ""} />
            <span>Refrescar</span>
          </button>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          {visibleGenerationProviders.map((prov) => {
            const userKeyInfo = userConnections.find((c) => c.provider_id === prov.id);
            const isConfigured = Boolean(userKeyInfo?.configured);
            const isEnv = Boolean(userKeyInfo?.is_env_fallback);
            const isEditing = editingProviderId === prov.id;

            return (
              <div
                key={prov.id}
                data-testid={`vault-provider-card-${prov.id}`}
                className="flex flex-col justify-between rounded-xl border border-[#262626] bg-[#161616] p-4"
              >
                <div>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <Key size={16} className={isConfigured ? "text-[#f5b544]" : "text-[#6b6b6b]"} />
                      <span className="font-semibold text-sm text-[#ededed]">{prov.label}</span>
                    </div>
                    {isConfigured ? (
                      isEnv ? (
                        <span className="rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-0.5 text-[11px] font-medium text-emerald-400">
                          Clave global (entorno)
                        </span>
                      ) : (
                        <span className="rounded-full border border-[#f5b544]/40 bg-[#f5b544]/10 px-2.5 py-0.5 text-[11px] font-medium text-[#f5b544]">
                          Clave configurada
                        </span>
                      )
                    ) : (
                      <span className="rounded-full border border-[#303030] bg-[#1c1c1c] px-2.5 py-0.5 text-[11px] font-medium text-[#8a8a8a]">
                        Sin clave configurada
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-xs text-[#8a8a8a]">
                    Capacidades: {prov.capabilities.map((c) => t.connections?.capabilities?.[c] || c).join(", ")}
                  </p>
                  <p className="mt-1 text-[11px] text-[#707070]">
                    {isConfigured
                      ? isEnv
                        ? "Proporcionada por el entorno del servidor del estudio."
                        : "Clave propia cifrada en tu bóveda personal."
                      : "Se requiere clave para generar con este proveedor."}
                  </p>
                </div>

                {/* Formulario de Entrada de Clave */}
                {isEditing ? (
                  <div className="mt-3 space-y-2 border-t border-[#262626] pt-3">
                    <div className="relative">
                      <input
                        type={showKeyText ? "text" : "password"}
                        value={keyInput}
                        onChange={(e) => setKeyInput(e.target.value)}
                        placeholder={`Pega tu clave de ${prov.label}`}
                        className="v-input pr-9 font-mono text-xs w-full"
                        data-testid={`vault-input-${prov.id}`}
                      />
                      <button
                        type="button"
                        onClick={() => setShowKeyText(!showKeyText)}
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#707070] hover:text-[#ededed]"
                      >
                        {showKeyText ? <EyeOff size={14} /> : <Eye size={14} />}
                      </button>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        disabled={savingKey || !keyInput.trim()}
                        onClick={() => handleSaveKey(prov.id)}
                        data-testid={`vault-save-btn-${prov.id}`}
                        className="v-btn-primary v-btn-sm text-xs"
                      >
                        {savingKey ? "Guardando..." : "Guardar Clave"}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setEditingProviderId(null);
                          setKeyInput("");
                        }}
                        className="v-btn-ghost v-btn-sm text-xs"
                      >
                        Cancelar
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="mt-4 flex items-center justify-between border-t border-[#222] pt-3">
                    <button
                      type="button"
                      onClick={() => {
                        setEditingProviderId(prov.id);
                        setKeyInput("");
                      }}
                      data-testid={`vault-edit-btn-${prov.id}`}
                      className="v-btn-secondary v-btn-sm text-xs"
                    >
                      {isConfigured ? (isEnv ? "Configurar clave propia" : "Reemplazar Clave") : "Configurar Clave"}
                    </button>
                    {isConfigured && !isEnv && (
                      <button
                        type="button"
                        onClick={() => setKeyToDelete(userKeyInfo)}
                        data-testid={`vault-delete-btn-${prov.id}`}
                        title="Eliminar clave propia"
                        className="rounded-md p-1.5 text-[#6b6b6b] hover:bg-[#222] hover:text-rose-400"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* SECCIÓN 2: Límite y Seguimiento de Gastos */}
      <div className="flex flex-col gap-3 rounded-xl border border-[#262626] bg-[#141414] p-5" data-testid="user-budget-section">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#222] pb-4">
          <div>
            <div className="flex items-center gap-2">
              <DollarSign size={17} className="text-[#f5b544]" />
              <h3 className="text-sm font-semibold text-[#ededed]">Control y Seguimiento de Gastos</h3>
              {budgetStatus?.period && (
                <span className="rounded-full border border-[#333] bg-[#1c1c1c] px-2 py-0.5 text-[11px] font-mono text-[#a3a3a3]">
                  Periodo {budgetStatus.period}
                </span>
              )}
            </div>
            <p className="mt-1 text-xs text-[#8a8a8a]">
              Configura y monitoriza tu límite mensual de uso dentro de VEYRA de forma individual.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                fetchBudgetStatus();
                if (showHistory) fetchBudgetHistory();
              }}
              disabled={loadingBudget}
              title="Actualizar datos de presupuesto"
              className="v-btn-ghost v-btn-sm inline-flex items-center gap-1.5"
            >
              <RefreshCw size={13} className={loadingBudget ? "animate-spin text-[#f5b544]" : ""} />
              <span>Actualizar</span>
            </button>
          </div>
        </div>

        {/* Métricas clave */}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <div className="rounded-lg border border-[#222] bg-[#181818] p-3">
            <div className="text-[11px] font-medium text-[#8a8a8a]">Mi límite mensual</div>
            <div className="mt-1.5 text-lg font-semibold text-[#ededed]" data-testid="budget-monthly-limit">
              {budgetStatus?.monthly_limit_usd !== null && budgetStatus?.monthly_limit_usd !== undefined
                ? `$${budgetStatus.monthly_limit_usd.toFixed(2)}`
                : <span className="text-xs text-[#707070]">Sin configurar</span>}
            </div>
            <div className="mt-1 text-[10px] text-[#6b6b6b]">
              {budgetStatus?.monthly_limit_usd === 0 ? "Generación bloqueada" : "Tope mensual fijado"}
            </div>
          </div>

          <div className="rounded-lg border border-[#222] bg-[#181818] p-3">
            <div className="text-[11px] font-medium text-[#8a8a8a]">Uso del mes</div>
            <div className="mt-1.5 text-lg font-semibold text-[#ededed]" data-testid="budget-spent">
              ${(budgetStatus?.spent_usd || 0).toFixed(2)}
            </div>
            <div className="mt-1 text-[10px] text-[#6b6b6b]">
              {budgetStatus?.has_provisional ? "Incluye importes provisionales" : "Consumo contabilizado"}
            </div>
          </div>

          <div className="rounded-lg border border-[#222] bg-[#181818] p-3">
            <div className="text-[11px] font-medium text-[#8a8a8a]">En proceso</div>
            <div className="mt-1.5 text-lg font-semibold text-amber-400" data-testid="budget-in-progress">
              ${(budgetStatus?.in_progress_usd || 0).toFixed(2)}
            </div>
            <div className="mt-1 text-[10px] text-[#6b6b6b]">
              Reservas activas en curso
            </div>
          </div>

          <div className="rounded-lg border border-[#222] bg-[#181818] p-3">
            <div className="text-[11px] font-medium text-[#8a8a8a]">Disponible según tu límite</div>
            <div className={`mt-1.5 text-lg font-semibold ${(budgetStatus?.available_usd || 0) > 0 ? "text-emerald-400" : "text-rose-400"}`} data-testid="budget-available">
              ${(budgetStatus?.available_usd || 0).toFixed(2)}
            </div>
            <div className="mt-1 text-[10px] text-[#6b6b6b]">
              {(budgetStatus?.available_usd || 0) <= 0 ? "Margen agotado" : "Margen disponible"}
            </div>
          </div>
        </div>

        {/* Formulario de configuración de límite */}
        <div className="mt-2 rounded-lg border border-[#222] bg-[#181818] p-3">
          {editingLimit ? (
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <div className="flex-1">
                <label className="text-[11px] text-[#a3a3a3]">Nuevo límite mensual (USD):</label>
                <div className="relative mt-1">
                  <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-[#707070]">$</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={limitInput}
                    onChange={(e) => setLimitInput(e.target.value)}
                    placeholder="0.00"
                    className="v-input pl-6 text-xs w-full"
                    data-testid="input-monthly-limit"
                  />
                </div>
              </div>
              <div className="flex items-center gap-2 pt-2 sm:pt-4">
                <button
                  type="button"
                  disabled={savingLimit}
                  onClick={handleSaveLimit}
                  className="v-btn-primary v-btn-sm text-xs"
                  data-testid="save-monthly-limit-btn"
                >
                  {savingLimit ? "Guardando..." : "Guardar Límite"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setEditingLimit(false);
                    setLimitInput(budgetStatus?.monthly_limit_usd !== null && budgetStatus?.monthly_limit_usd !== undefined ? String(budgetStatus.monthly_limit_usd) : "");
                  }}
                  className="v-btn-ghost v-btn-sm text-xs"
                >
                  Cancelar
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="text-xs text-[#a3a3a3]">
                {budgetStatus?.monthly_limit_usd === null || budgetStatus?.monthly_limit_usd === undefined ? (
                  <span className="text-amber-400">Sin límite configurado: la generación con IA está bloqueada hasta configurar un límite mensual mayor a cero.</span>
                ) : budgetStatus.monthly_limit_usd === 0 ? (
                  <span className="text-rose-400">Límite configurado en $0.00: la generación está bloqueada.</span>
                ) : (
                  <span>Límite activo: <strong>${budgetStatus.monthly_limit_usd.toFixed(2)} USD</strong> mensuales.</span>
                )}
              </div>
              <button
                type="button"
                onClick={() => setEditingLimit(true)}
                className="v-btn-secondary v-btn-sm text-xs"
                data-testid="edit-monthly-limit-btn"
              >
                {budgetStatus?.monthly_limit_usd === null || budgetStatus?.monthly_limit_usd === undefined ? "Configurar Límite" : "Modificar Límite"}
              </button>
            </div>
          )}
        </div>

        {/* Nota explicativa oficial */}
        <div className="rounded-lg border border-[#2b2b2b]/60 bg-[#121212] p-3 text-[11px] leading-relaxed text-[#7a7a7a]">
          <p>
            {budgetStatus?.disclaimer || "Este es un límite de control de uso interno dentro de VEYRA, no representa el saldo de tu cuenta en Apimart ni una garantía sobre su facturación externa."}
          </p>
          <p className="mt-1 text-[#666]">
            Los costes confirmados por el proveedor se liquidan atómicamente. Si un trabajo queda pendiente o el proveedor no confirma el coste final, se conserva la reserva como coste provisional claramente identificado.
          </p>
        </div>

        {/* Toggle Historial de uso */}
        <div className="pt-2">
          <button
            type="button"
            onClick={() => {
              if (!showHistory && budgetHistory.length === 0) {
                fetchBudgetHistory();
              }
              setShowHistory(!showHistory);
            }}
            data-testid="toggle-budget-history-btn"
            className="inline-flex items-center gap-1.5 text-xs text-[#a3a3a3] hover:text-[#ededed]"
          >
            <History size={13} />
            <span>{showHistory ? "Ocultar historial de uso del mes" : "Ver historial de uso del mes"}</span>
          </button>

          {showHistory && (
            <div className="mt-3 rounded-lg border border-[#222] bg-[#161616] p-3" data-testid="budget-history-table">
              {loadingHistory ? (
                <div className="py-4 text-center text-xs text-[#707070]">Cargando historial...</div>
              ) : budgetHistory.length === 0 ? (
                <div className="py-4 text-center text-xs text-[#707070]">No hay registros de consumo en este periodo.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs">
                    <thead>
                      <tr className="border-b border-[#262626] text-[#707070]">
                        <th className="pb-2 font-medium">Trabajo</th>
                        <th className="pb-2 font-medium">Fecha</th>
                        <th className="pb-2 font-medium">Modelo</th>
                        <th className="pb-2 font-medium">Estado</th>
                        <th className="pb-2 font-medium text-right">Coste</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#1f1f1f]">
                      {budgetHistory.map((item) => {
                        const rawDate = item.created_at || item.date;
                        const dateDisplay = (() => {
                          if (!rawDate) return <span className="text-[#606060]" title="Fecha no disponible en el registro del trabajo">No disponible</span>;
                          try {
                            const d = new Date(rawDate);
                            if (isNaN(d.getTime())) return <span className="text-[#606060]" title="Formato de fecha inválido">No disponible</span>;
                            return d.toLocaleString(undefined, {
                              year: "numeric",
                              month: "2-digit",
                              day: "2-digit",
                              hour: "2-digit",
                              minute: "2-digit",
                              second: "2-digit",
                            });
                          } catch {
                            return <span className="text-[#606060]" title="Error al formatear la fecha">No disponible</span>;
                          }
                        })();

                        const costDisplay = (() => {
                          const val = item.amount_usd !== undefined && item.amount_usd !== null
                            ? item.amount_usd
                            : (item.cost !== undefined && item.cost !== null
                              ? item.cost
                              : (item.cost_display ? item.cost_display : null));
                          if (val === null || val === undefined || (typeof val === "number" && isNaN(val))) {
                            return <span className="text-[#606060]" title="Coste no disponible en el registro del trabajo">No disponible</span>;
                          }
                          if (typeof val === "string" && val.startsWith("$")) {
                            return val;
                          }
                          const num = Number(val);
                          if (isNaN(num)) {
                            return <span className="text-[#606060]" title="Importe no numérico">No disponible</span>;
                          }
                          const formatted = num.toLocaleString("en-US", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 6,
                          });
                          return `$${formatted} USD`;
                        })();

                        const statusDisplay = (() => {
                          const isProvisional = item.provisional === true || item.status === "provisional" || item.cost_type === "provisional";
                          const isConfirmed = item.status === "confirmed" && !isProvisional;
                          const isActive = item.status === "active";
                          const isReleased = item.status === "released";

                          if (isConfirmed) {
                            return (
                              <span className="rounded-full px-2 py-0.5 text-[10px] font-medium bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                                Confirmado
                              </span>
                            );
                          }
                          if (isProvisional) {
                            return (
                              <span className="rounded-full px-2 py-0.5 text-[10px] font-medium bg-blue-500/10 text-blue-400 border border-blue-500/20">
                                Provisional
                              </span>
                            );
                          }
                          if (isActive) {
                            return (
                              <span className="rounded-full px-2 py-0.5 text-[10px] font-medium bg-amber-500/10 text-amber-400 border border-amber-500/20">
                                En proceso
                              </span>
                            );
                          }
                          if (isReleased) {
                            return (
                              <span className="rounded-full px-2 py-0.5 text-[10px] font-medium bg-[#262626] text-[#707070]">
                                Liberado
                              </span>
                            );
                          }
                          return (
                            <span className="rounded-full px-2 py-0.5 text-[10px] font-medium bg-[#262626] text-[#707070]" title="Estado del registro">
                              {item.cost_label || item.status || "No disponible"}
                            </span>
                          );
                        })();

                        return (
                          <tr key={item.job_id || item.reservation_id} className="hover:bg-[#1a1a1a]">
                            <td className="py-2 font-mono text-[11px] text-[#ededed]">
                              {(item.job_id || "").slice(0, 8) || "—"}
                            </td>
                            <td className="py-2 text-[11px] text-[#8a8a8a]">
                              {dateDisplay}
                            </td>
                            <td className="py-2 text-[11px] text-[#ededed]">
                              {item.model_id || <span className="text-[#606060]" title="Modelo no especificado">No disponible</span>}
                            </td>
                            <td className="py-2">
                              {statusDisplay}
                            </td>
                            <td className="py-2 text-right font-mono text-[11px] text-[#ededed]">
                              {costDisplay}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* SECCIÓN 3: Conexiones del Estudio (Compatibilidad local) */}
      <div className="flex flex-col gap-3 pt-4 border-t border-[#262626]">
        <Notice testId="connections-manager-notice">{t.settings.connectionsBody}</Notice>
        <div>
          <div className="mb-1.5 text-xs font-medium text-[#a3a3a3]">{t.connections.lifecycleNote}</div>
          <LifecycleStrip />
        </div>

        {visibleStudioConnections.length === 0 ? (
          <EmptyState icon={Plug} title={t.settings.connectionsEmpty} body={t.settings.connectionsEmptyBody} testId="connections-empty" />
        ) : (
          <ul className="flex flex-col gap-2" data-testid="connections-list">
            {visibleStudioConnections.map((c) => {
              const provInfo = catalog?.providers?.find((p) => p.provider_id === c.provider);
              const userKey = userConnections.find((uk) => uk.provider_id === c.provider);
              const isConfigured = Boolean(userKey?.configured || provInfo?.key_configured || c.status === "configured");
              const isVerified = Boolean(c.verified || c.status === "verified");

              return (
                <li key={c.id} data-testid={`connection-row-${c.id}`} className="flex items-center gap-3 rounded-lg border border-[#303030] bg-[#151515] px-3 py-2.5">
                  <Plug size={15} className={`shrink-0 ${isVerified ? "text-[#4ade80]" : isConfigured ? "text-[#f5b544]" : "text-[#6b6b6b]"}`} />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium">{c.name}</div>
                    <div className="text-xs text-[#a3a3a3]">{c.provider} · {(c.capabilities || []).map((cap) => t.connections.capabilities[cap] || cap).join(" · ")}</div>
                  </div>
                  {isVerified ? (
                    <span data-testid={`connection-status-${c.id}`} className="flex items-center gap-1.5 whitespace-nowrap text-xs text-[#4ade80]">
                      <span className="h-1.5 w-1.5 rounded-full bg-[#4ade80]" />
                      {t.connections.status.verified}
                    </span>
                  ) : isConfigured ? (
                    <span data-testid={`connection-status-${c.id}`} className="flex items-center gap-1.5 whitespace-nowrap text-xs text-[#f5b544]">
                      <span className="h-1.5 w-1.5 rounded-full bg-[#f5b544]" />
                      {t.connections.status.configured}
                    </span>
                  ) : (
                    <span data-testid={`connection-status-${c.id}`} className="flex items-center gap-1.5 whitespace-nowrap text-xs text-[#a3a3a3]">
                      <span className="h-1.5 w-1.5 rounded-full bg-[#6b6b6b]" />
                      {t.connections.status.not_configured}
                    </span>
                  )}
                  <button data-testid={`connection-delete-${c.id}`} onClick={() => setToDelete(c)} className="rounded-md p-1.5 text-[#6b6b6b] hover:bg-[#222] hover:text-[#e5655a]" aria-label={t.settings.deleteConnection} title={t.settings.deleteConnection}><Trash2 size={14} /></button>
                </li>
              );
            })}
          </ul>
        )}

        <div className="flex flex-wrap items-end gap-2">
          <label className="flex-1 min-w-[180px]">
            <span className="mb-1.5 block text-sm font-medium">{t.settings.connectionName}</span>
            <input data-testid="settings-connection-name" className="v-input" placeholder={t.settings.connectionNamePh} value={name} onChange={(e) => setName(e.target.value)} />
          </label>
          <label className="min-w-[140px]">
            <span className="mb-1.5 block text-sm font-medium">{t.settings.connectionProvider}</span>
            <select data-testid="settings-connection-provider" className="v-input" value={providerId} onChange={(e) => setProviderId(e.target.value)}>
              {visibleConnectionProviders.map((p) => <option key={p.id} value={p.id}>{p.label}{!p.available ? ` — ${t.common.pending}` : ""}</option>)}
            </select>
          </label>
          <button data-testid="settings-add-connection" className="v-btn-primary v-btn-sm shrink-0" disabled={!provider?.available} title={!provider?.available ? t.settings.providerPendingNote : undefined} onClick={add}><Plus size={14} />{t.settings.addConnection}</button>
        </div>
        {!provider?.available && <Notice tone="warn" testId="settings-provider-pending-notice">{t.settings.providerPendingNote}</Notice>}
        <p className="text-xs text-[#a3a3a3]">{t.settings.connectionProviderNote}</p>
      </div>

      {/* Diálogos de Confirmación */}
      <ConfirmDialog
        open={Boolean(toDelete)}
        onOpenChange={(o) => !o && setToDelete(null)}
        title={toDelete?.name || ""}
        body={t.settings.deleteConnectionConfirm}
        onConfirm={() => dispatch({ type: "CONNECTION_DELETE", payload: { id: toDelete.id } })}
      />

      <ConfirmDialog
        open={Boolean(keyToDelete)}
        onOpenChange={(o) => !o && setKeyToDelete(null)}
        title={`¿Eliminar clave propia de ${keyToDelete?.provider_id}?`}
        body="Tu clave cifrada se eliminará de la bóveda personal. Si tienes tareas activas vinculadas a este proveedor, la operación será rechazada."
        confirmLabel="Eliminar clave"
        onConfirm={handleDeleteKey}
      />
    </div>
  );
};

export default ConnectionsManager;
